import type { Area } from './types.js';
import { bboxOfPoly, pointInPoly } from './geom.js';
import type { Heightfield } from './heightfield.js';

/**
 * What height each body of water sits at.
 *
 * Water was drawn the way every other fill is drawn — draped over the terrain and then
 * lifted until it cleared the ground mesh — and for a park that is right, because a park
 * *is* a coloured patch of ground. A river is not. It is a level surface with things
 * crossing over it, and treating it as a blanket laid on a lumpy field produced two
 * failures at once in Le Marais:
 *
 *  - the Seine tilted, following the DEM's noise across half a metre of its width, and
 *  - the lift needed to keep it out of the ground put the surface *above* every bridge
 *    over it. Sixteen of the eighteen named Seine crossings had deck triangles under
 *    water, the worst by 1.23 m, and the car — which is drawn at terrain height — drove
 *    the whole span submerged.
 *
 * So each polygon gets one number and is drawn flat at it, and everything that has to
 * stay clear of the water (see `decks.ts`) has one number to stay clear of.
 *
 * **Choosing the number** is the whole of the problem. A DEM samples a river as its
 * *surface*, so the heights inside the polygon are bimodal: a large flat cluster at water
 * level, plus a tail climbing the banks wherever the polygon's edge runs up the quay and
 * the 11 m smoothing drags the wall inwards. The median lands in the middle of the flat
 * cluster, which leaves half the channel bed poking through; the maximum lands at the top
 * of the bank tail, which floods the quay. `WATER_LEVEL_QUANTILE` is picked to sit just
 * above the flat cluster and well below the tail — for the Seine that is a 0.35 m window,
 * for the Vistula 1.6 m, and 0.9 lands inside both.
 *
 * Where the bank still comes through, it comes through at the *edge*, and reads as a
 * shoal or a slipway rather than as a hole torn in the river. That is the deliberate
 * trade: a flat river with an honest shoreline beats a tilted one that swallows bridges.
 */
export const WATER_LEVEL_QUANTILE = 0.9;

/**
 * Above the sampled quantile, in metres.
 *
 * The drawn ground is a coarser triangulation of the same field and disagrees with it by
 * centimetres either way; this is the margin that keeps those disagreements under the
 * water rather than through it.
 */
export const WATER_SURFACE_CLEARANCE = 0.3;

/** Never fewer than this many interior samples before the quantile means anything. */
const MIN_SAMPLES = 6;

/**
 * When a body of water is not level at all, and gets no level.
 *
 * Everything above assumes a lake or a big lowland river, whose surface really is one height
 * across the region. A stream running down a valley is not: the Rawa falls 7.5 m across
 * central Katowice. One level for it is the level of its top end, so from there on it hung
 * in the air as a blue ribbon, and every short bridge over it had to clear that ribbon and
 * became an 8 m ski jump.
 *
 * Such a body is told apart by how much of it lies well under the level the quantile picked:
 * a bank tail is above the level by construction, so ground a metre *below* it is the water
 * itself falling away. On the fixtures and the Polish tours that share is never above 0.06
 * for a lake, a pond, the Seine or the Vistula, and 0.33 to 0.83 for the Rawa and its arms.
 *
 * A body like that is left out of the levels, which puts it back on the draped path every
 * other fill takes: it follows the ground down the valley, and a bridge over it is sized by
 * the ground under it, as for any bridge over something it cannot see.
 */
const FALLING_DROP = 1;
const FALLING_SHARE = 0.25;

/**
 * Sampling step in metres, and the cap on how many samples one polygon may cost.
 *
 * The Seine's footprint is 513,000 m², so a fixed fine step would be six figures of
 * point-in-polygon tests on a mesh build. The step widens for a large body — its surface
 * is flat, so there is nothing there a finer grid would find.
 */
const SAMPLE_STEP = 6;
const MAX_SAMPLES = 4096;

/**
 * The level of every `water` area in the world, by area id — every one that has a level;
 * see `FALLING_SHARE` for the ones that do not.
 *
 * Flat worlds are absent from the map entirely rather than present at zero: a region with
 * no DEM must emit exactly the geometry it emitted before terrain existed, and that is
 * the draped-fill path, not this one.
 */
export function waterLevels(areas: Area[], terrain: Heightfield): Map<number, number> {
  const levels = new Map<number, number>();
  if (terrain.isFlat) return levels;
  for (const area of areas) {
    if (area.kind !== 'water') continue;
    const level = waterLevelOf(area, terrain);
    if (level !== null) levels.set(area.id, level);
  }
  return levels;
}

function waterLevelOf(area: Area, terrain: Heightfield): number | null {
  const bb = bboxOfPoly(area.poly);
  const spanX = Math.max(1e-3, bb.maxX - bb.minX);
  const spanZ = Math.max(1e-3, bb.maxZ - bb.minZ);
  // Widened until the grid fits inside the sample budget, and never coarser than the
  // polygon itself — a 12 m pond still has to get a few samples in it.
  let step = SAMPLE_STEP;
  while ((spanX / step + 1) * (spanZ / step + 1) > MAX_SAMPLES) step *= 2;
  step = Math.min(step, spanX / 3, spanZ / 3);

  const heights: number[] = [];
  for (let x = bb.minX; x <= bb.maxX; x += step) {
    for (let z = bb.minZ; z <= bb.maxZ; z += step) {
      if (!pointInPoly(area.poly, x, z)) continue;
      // An island is not water, and its ground would drag the quantile up the bank.
      if (area.holes.some((h) => pointInPoly(h, x, z))) continue;
      heights.push(terrain.heightAt(x, z));
    }
  }

  // A polygon too small or too thin to catch the grid falls back to its own outline,
  // which for a pond that size is the same surface anyway. Too small to be a stream as well:
  // an outline runs up the bank all the way round, so it always looks like it falls.
  const outline = heights.length < MIN_SAMPLES;
  if (outline) {
    heights.length = 0;
    for (let i = 0; i < area.poly.length; i += 2) {
      heights.push(terrain.heightAt(area.poly[i], area.poly[i + 1]));
    }
  }
  if (!heights.length) return terrain.heightAt(bb.minX, bb.minZ) + WATER_SURFACE_CLEARANCE;

  heights.sort((a, b) => a - b);
  const at = Math.min(heights.length - 1, Math.floor(WATER_LEVEL_QUANTILE * heights.length));
  const level = heights[at];
  let under = 0;
  while (under < heights.length && heights[under] < level - FALLING_DROP) under++;
  if (!outline && under / heights.length > FALLING_SHARE) return null;
  return level + WATER_SURFACE_CLEARANCE;
}

/**
 * Which water body, if any, covers a point — by area id, so a caller can look the level up.
 *
 * Built once and queried per sample along a bridge; the grid is what stops a 99-vertex
 * river polygon being tested against every metre of every deck in the city.
 */
export class WaterIndex {
  private readonly grid = new Map<number, number[]>();
  private readonly areas: Area[] = [];
  private static readonly CELL = 64;

  constructor(areas: Area[]) {
    for (const area of areas) {
      if (area.kind !== 'water') continue;
      const index = this.areas.length;
      this.areas.push(area);
      const bb = bboxOfPoly(area.poly);
      const cell = WaterIndex.CELL;
      for (let cx = Math.floor(bb.minX / cell); cx <= Math.floor(bb.maxX / cell); cx++) {
        for (let cz = Math.floor(bb.minZ / cell); cz <= Math.floor(bb.maxZ / cell); cz++) {
          const key = cx * 100003 + cz;
          const bucket = this.grid.get(key);
          if (bucket) bucket.push(index);
          else this.grid.set(key, [index]);
        }
      }
    }
  }

  /** The covering water area, or null. Holes count as land, because they are islands. */
  areaAt(x: number, z: number): Area | null {
    const cell = WaterIndex.CELL;
    const bucket = this.grid.get(Math.floor(x / cell) * 100003 + Math.floor(z / cell));
    if (!bucket) return null;
    for (const index of bucket) {
      const area = this.areas[index];
      if (!pointInPoly(area.poly, x, z)) continue;
      if (area.holes.some((h) => pointInPoly(h, x, z))) continue;
      return area;
    }
    return null;
  }
}
